import { Button as BaseButton } from "@base-ui/react/button";
import {
	Children,
	type ElementRef,
	Fragment,
	isValidElement,
	type ReactNode,
	type Ref,
} from "react";

import { cn } from "@/shared/lib/utils";
import type { ButtonProps } from "./button-types";
import { buttonVariants } from "./button-variants";

const TRUNCATE_LABEL_CLASS = "min-w-0 truncate" as const;

const graphemeSegmenter =
	typeof Intl !== "undefined" && Intl.Segmenter
		? new Intl.Segmenter("en", { granularity: "grapheme" })
		: null;

// Module-level cache for grapheme count results
const graphemeCountCache = new Map<string, number>();
const GRAPHEME_CACHE_MAX_SIZE = 500;

/**
 * Check if a string is a single grapheme cluster (e.g., a single emoji or character).
 * Uses Intl.Segmenter for proper grapheme detection, with fallback for older environments.
 * Results are cached for performance.
 */
function isSingleGrapheme(str: string): boolean {
	if (graphemeSegmenter) {
		// Check cache first
		let count = graphemeCountCache.get(str);
		if (count === undefined) {
			const segments = [...graphemeSegmenter.segment(str)];
			count = segments.length;
			// Limit cache size to prevent memory leaks
			if (graphemeCountCache.size >= GRAPHEME_CACHE_MAX_SIZE) {
				const firstKey = graphemeCountCache.keys().next().value;
				if (firstKey !== undefined) {
					graphemeCountCache.delete(firstKey);
				}
			}
			graphemeCountCache.set(str, count);
		}
		return count === 1;
	}
	// Fallback: check if string length is <= 2 (covers most single emojis)
	// This is imperfect but handles common cases when Intl.Segmenter is unavailable
	return str.length <= 2;
}

function processButtonChildren(children: ReactNode): ReactNode {
	return Children.map(children, (child) => {
		if (typeof child === "string" || typeof child === "number") {
			const strValue = String(child);
			// Don't truncate single graphemes (emojis, single characters)
			// They should display fully as icons, not be truncated
			if (isSingleGrapheme(strValue.trim())) {
				return <span className="flex-shrink-0">{child}</span>;
			}
			return <span className={TRUNCATE_LABEL_CLASS}>{child}</span>;
		}
		if (
			isValidElement<{ children?: ReactNode }>(child) &&
			child.type === Fragment
		) {
			return <>{processButtonChildren(child.props.children)}</>;
		}
		return child;
	});
}

const Button = ({
	className,
	variant,
	size,
	render,
	ref,
	children,
	nativeButton,
	...props
}: ButtonProps & {
	ref?: Ref<ElementRef<"button"> | null>;
}) => {
	// Workaround: CSS cascade layer ordering can cause @layer base border-color
	// to override @layer utilities border-color classes. Inline style ensures
	// outline variant buttons always have a visible border.
	const borderStyle =
		variant === "outline"
			? { border: "1px solid hsl(var(--input))", ...props.style }
			: props.style;

	return (
		<BaseButton
			className={cn(buttonVariants({ variant, size, className }))}
			data-slot="button"
			nativeButton={nativeButton}
			ref={ref}
			render={render}
			{...props}
			style={borderStyle}
		>
			{processButtonChildren(children)}
		</BaseButton>
	);
};

Button.displayName = "Button";

export { Button };
